import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import '../../styles/publicReveal.css';

export type PublicRevealAnimation =
  | 'fade-in'
  | 'fade-in-up'
  | 'fade-in-down'
  | 'fade-in-left'
  | 'fade-in-right';

type Props = {
  children: ReactNode;
  animation?: PublicRevealAnimation;
  /** Délai avant l’animation (ms). */
  delay?: number;
  /** Durée de l’animation (ms). */
  duration?: number;
  className?: string;
  /** Ne jouer l’animation qu’une fois (défaut). */
  once?: boolean;
};

export function publicRevealStagger(index: number, stepMs = 80, baseMs = 0) {
  return baseMs + index * stepMs;
}

export default function PublicReveal({
  children,
  animation = 'fade-in-up',
  delay = 0,
  duration = 3000,
  className = '',
  once = true,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      const reduced = mq.matches;
      setReduceMotion(reduced);
      if (reduced) setVisible(true);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { root: null, rootMargin: '0px 0px -6% 0px', threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, reduceMotion]);

  const style = {
    '--public-reveal-delay': `${delay}ms`,
    '--public-reveal-duration': `${duration}ms`,
  } as CSSProperties;

  const classes = [
    'public-reveal',
    `public-reveal--${animation}`,
    visible || reduceMotion ? 'public-reveal--visible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} style={style}>
      {children}
    </div>
  );
}
